import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { GridActionsCellItem, itIT } from '@mui/x-data-grid';
import Icon from '../../components/Icon';
import { Box } from '@mui/system';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { StripedDataGrid } from '../../components/DataGrid/StripedDataGrid';
import { CurrencyCell } from '../../components/Cells/CurrencyCell';
import { openPreview } from '../../utils/API/fileDownload';
import { error } from '../../utils/notification';
import { useTranslation } from 'react-i18next';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const ExpensesDataGrid = ({
  data,
  onEditClick,
  onDeleteClick,
  isReportConfirmed,
  isAdminViewingOther,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = (path) => {
    setIsLoading(true);
    try {
      openPreview(path);
      setTimeout(() => setIsLoading(false), 500);
    } catch (e) {
      error(`Error downloading file: ${e}`);
      setIsLoading(false);
    }
  };

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'date',
        headerName: 'data',
        flex: 1.5,
      },
      {
        field: 'description',
        headerName: 'Descrizione',
        flex: 2,
      },
      {
        field: 'type',
        headerName: 'Tipologia Spesa',
        flex: 1.5,
        renderCell: ({ value }) => t(value),
      },
      {
        field: 'cost',
        headerName: 'Spesa',
        flex: 1.5,
        renderCell: CurrencyCell,
      },
      {
        field: 'attachment',
        headerName: 'File',
        flex: 2,
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            {row.attachment && (
              <Tooltip
                arrow
                title={
                  <span style={{ color: 'black' }}>Fattura o scontrino</span>
                }
              >
                <span>
                  <GridActionsCellItem
                    label="Scarica Fattura"
                    icon={
                      <Icon
                        name="tabler:file-download"
                        size="20px"
                        color="#005EB8"
                      />
                    }
                    onClick={() => handleDownload(row.attachment)}
                  />
                </span>
              </Tooltip>
            )}
            {row.attachment_receipt && (
              <Tooltip
                arrow
                title={
                  <span style={{ color: 'black' }}>Pagamento elettronico</span>
                }
              >
                <span>
                  <GridActionsCellItem
                    label="Scarica Ricevuta"
                    icon={
                      <Icon
                        name="tabler:file-download"
                        size="20px"
                        color="#005EB8"
                      />
                    }
                    onClick={() => handleDownload(row.attachment_receipt)}
                  />
                </span>
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'invoice_made_out_to_company',
        headerName: 'Fatture I3',
        flex: 1.5,
        renderCell: ({ value }) => (value ? 'Si' : 'No'),
      },
      {
        field: 'actions',
        headerName: 'Azioni',
        type: 'actions',
        cellClassName: 'actions',
        width: 150,
        getActions: ({ id }) =>
          [
            <GridActionsCellItem
              icon={
                !isReportConfirmed && !isAdminViewingOther ? (
                  <EditOutlinedIcon />
                ) : (
                  <VisibilityOutlinedIcon />
                )
              }
              label="Edit"
              onClick={(event) => {
                event.stopPropagation();
                onEditClick(id);
              }}
              color="inherit"
              key={`edit_${id}`}
            />,
            !isReportConfirmed && !isAdminViewingOther && (
              <GridActionsCellItem
                icon={<DeleteOutlinedIcon />}
                label="Delete"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteClick(id);
                }}
                color="inherit"
                key={`delete_${id}`}
              />
            ),
          ].filter(Boolean),
      },
    ];

    return cols;
  }, [onEditClick, onDeleteClick, isReportConfirmed, isAdminViewingOther, t]);

  const rows = useMemo(() => JSON.parse(JSON.stringify(data)), [data]);

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            pointerEvents: 'none',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <StripedDataGrid
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        density="standard"
        disableColumnMenu
        disableRowSelectionOnClick
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        getRowHeight={() => 'auto'}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'name',
                sort: 'asc',
              },
            ],
          },
          pagination: {
            paginationModel: { pageSize: 100 },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
            py: '15px',
          },
          '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
            py: '22px',
          },
        }}
      />
    </Box>
  );
};

ExpensesDataGrid.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEditClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  isReportConfirmed: PropTypes.bool.isRequired,
  isAdminViewingOther: PropTypes.bool.isRequired,
};

export default ExpensesDataGrid;
